/**
 * Tracks Page
 *
 * Displays all tracks with the same toolbar UX as the item detail view:
 * URL-synced search/sort/group, column visibility, density, list/table toggle.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Layers,
  RefreshCcw,
  Target,
  Plus,
  ChevronRight,
  Star,
  Lock,
  X,
  Unlink,
} from 'lucide-react';
import { PageHeader as SharedPageHeader } from '@/components/prioritization/PageHeader';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useAllSessions } from '@/stores/prioritization/sessionStore';
import { usePinnedCycleStore } from '@/stores/prioritization/pinnedCycleStore';
import { createTrackWithFeedback } from '@/lib/tracks/createTrackFlow';
import { CREATE_TRACK_LABEL, CREATE_TRACK_DIALOG_TITLE } from '@/lib/tracks/vocabulary';
import type { Track, TrackStatus } from '@/types/prioritization/track';
import { useEntityListState } from '@/hooks/useEntityListState';
import { EntityListToolbar } from '@/components/shared/EntityListToolbar';
import { VirtualizedTable } from '@/components/collection/VirtualizedTable';
import type { VirtualizedTableColumn } from '@/components/collection/VirtualizedTable';
import { Modal, TextInput, TextArea, Select, Field, Callout, EmptyState } from '@/components/ds';
import {
  panel,
  panelInset,
  status,
  chip,
  btnPrimary,
  btnSecondary,
  LABEL_MONO,
  FOCUS_RING,
  type Tone,
} from '@/components/mono/primitives';

// ── Status helpers ────────────────────────────────────────────────────────────

/**
 * Status is READ, not pressed, so it does not borrow the button's box — the
 * affordance rule in mono/primitives. These were tinted pills, visually
 * identical to the buttons beside them.
 */
const STATUS_TONE: Record<TrackStatus, { label: string; tone: Tone }> = {
  active:         { label: 'Active',   tone: 'green' },
  accepted:       { label: 'Accepted', tone: 'slate' },
  final_rejected: { label: 'Rejected', tone: 'coral' },
  archived:       { label: 'Archived', tone: 'neutral' },
};

function TrackStatusBadge({ status: trackStatus }: { status: TrackStatus }) {
  const s = STATUS_TONE[trackStatus] ?? STATUS_TONE.active;
  return <span {...status(s.tone)}>{s.label}</span>;
}

function formatDate(v: Date | string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString();
}

// ── Column definitions ────────────────────────────────────────────────────────

const ALL_COLUMNS = [
  { key: 'name',      label: 'Track', defaultVisible: true  },
  { key: 'cycle',     label: 'Cycle',    defaultVisible: true  },
  { key: 'sessions',  label: 'Sessions', defaultVisible: true  },
  { key: 'status',    label: 'Status',   defaultVisible: true  },
  { key: 'createdAt', label: 'Created',  defaultVisible: true  },
  { key: 'updatedAt', label: 'Modified', defaultVisible: false },
];

const SORT_OPTIONS = [
  { field: 'name',      label: 'Name (A–Z)' },
  { field: 'cycle',     label: 'Cycle' },
  { field: 'status',    label: 'Status' },
  { field: 'createdAt', label: 'Date Created' },
  { field: 'updatedAt', label: 'Last Modified' },
];

const GROUP_OPTIONS = [
  { field: 'cycleId', label: 'Cycle' },
  { field: 'status',  label: 'Status' },
];

const FILTER_FIELDS = [
  {
    key: 'status',
    label: 'Status',
    options: [
      { value: 'active',         label: 'Active' },
      { value: 'accepted',       label: 'Accepted' },
      { value: 'final_rejected', label: 'Rejected' },
      { value: 'archived',       label: 'Archived' },
    ],
  },
];

const DEFAULT_SORT_FIELD = 'cycle';
const DEFAULT_SORT_DIR = 'asc' as const;

// ── Filtering + sorting helpers ───────────────────────────────────────────────

function matchesSearch(track: Track, q: string, cycleMap: Record<string, string>): boolean {
  if (!q) return true;
  const lower = q.toLowerCase();
  return track.name.toLowerCase().includes(lower) ||
    (track.description ?? '').toLowerCase().includes(lower) ||
    (track.cycleId ? cycleMap[track.cycleId] ?? '' : 'unbound').toLowerCase().includes(lower);
}

function sortTracks(tracks: Track[], field: string, dir: 'asc' | 'desc', cycleMap: Record<string, string>): Track[] {
  return [...tracks].sort((a, b) => {
    let valA: string | number = '';
    let valB: string | number = '';
    if (field === 'name')      { valA = a.name; valB = b.name; }
    else if (field === 'cycle')     { valA = a.cycleId ? (cycleMap[a.cycleId] ?? '') : ''; valB = b.cycleId ? (cycleMap[b.cycleId] ?? '') : ''; }
    else if (field === 'status')    { valA = a.status; valB = b.status; }
    else if (field === 'createdAt') { valA = new Date(a.createdAt).getTime(); valB = new Date(b.createdAt).getTime(); }
    else if (field === 'updatedAt') { valA = new Date(a.updatedAt).getTime(); valB = new Date(b.updatedAt).getTime(); }
    if (valA < valB) return dir === 'asc' ? -1 : 1;
    if (valA > valB) return dir === 'asc' ? 1 : -1;
    return 0;
  });
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function TracksPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createMode, setCreateMode] = useState<'cycle_linked' | 'standalone'>('cycle_linked');
  const [newTrackCycleId, setNewTrackCycleId] = useState('');
  const [newTrackName, setNewTrackName] = useState('');
  const [newTrackDescription, setNewTrackDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const urlCycleId = searchParams.get('cycleId');
  const pinnedCycleId = usePinnedCycleStore(s => s.pinnedCycleId);
  const pinnedCycleName = usePinnedCycleStore(s => s.pinnedCycleName);
  const pinnedTrackId = usePinnedCycleStore(s => s.pinnedTrackId);
  const enterTrack = usePinnedCycleStore(s => s.enterTrack);
  const exitTrack = usePinnedCycleStore(s => s.exitTrack);
  const activeCycleId = urlCycleId ?? pinnedCycleId;

  const tracks = useTrackStore(s => s.tracks);
  // The list's own read, not the store-wide isLoading that every track read
  // flips: a failed or stalled read ends in "could not load" + Retry (audit #37).
  const allTracksStatus = useTrackStore(s => s.allTracksStatus);
  const allTracksError = useTrackStore(s => s.allTracksError);
  const fetchAllTracks = useTrackStore(s => s.fetchAllTracks);
  const getNextTrackName = useTrackStore(s => s.getNextTrackName);

  const cycles = useDegapProcessStore(s => s.cycles);
  const fetchCycles = useDegapProcessStore(s => s.fetchFromSupabase);
  const activeCycles = useMemo(() => cycles.filter(c => c.status === 'active' || c.status === 'draft'), [cycles]);

  const sessions = useAllSessions() ?? [];

  useEffect(() => { fetchAllTracks(); fetchCycles(); }, [fetchAllTracks, fetchCycles]);

  // ?create=1 (the + in the track workspace rail) opens the create dialog,
  // then leaves the URL so a reload does not open it again.
  useEffect(() => {
    if (searchParams.get('create') !== '1') return;
    setShowCreateModal(true);
    const next = new URLSearchParams(searchParams);
    next.delete('create');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (showCreateModal && activeCycleId) setNewTrackCycleId(activeCycleId);
  }, [showCreateModal, activeCycleId]);

  useEffect(() => {
    if (newTrackCycleId) {
      const cycleName = cycles.find(c => c.id === newTrackCycleId)?.name ?? 'Track';
      setNewTrackName(getNextTrackName(newTrackCycleId, cycleName));
    } else if (createMode === 'standalone') {
      // For standalone tracks, suggest a generic name based on count
      const standaloneCount = tracks.filter(t => t.isStandalone).length;
      setNewTrackName(standaloneCount === 0 ? 'Track 1' : `Track ${standaloneCount + 1}`);
    } else {
      setNewTrackName('');
    }
  }, [newTrackCycleId, createMode, cycles, tracks, getNextTrackName]);

  const handleCreateTrack = async () => {
    if (!newTrackName.trim() || isCreating) return;
    if (createMode === 'cycle_linked' && !newTrackCycleId) return;
    setIsCreating(true);
    setCreateError(null);
    let result;
    if (createMode === 'standalone') {
      result = await createTrackWithFeedback({
        name: newTrackName.trim(),
        description: newTrackDescription.trim() || undefined,
        isStandalone: true,
        scopeType: 'default',
        scopeLabel: 'Default',
      });
    } else {
      const existingDefaults = tracks.filter(t => t.cycleId === newTrackCycleId && t.scopeType === 'default');
      result = await createTrackWithFeedback({
        cycleId: newTrackCycleId, name: newTrackName.trim(),
        description: newTrackDescription.trim() || undefined,
        scopeType: existingDefaults.length > 0 ? 'custom_group' : 'default',
        scopeLabel: existingDefaults.length > 0 ? newTrackName.trim() : 'Default',
      });
    }
    setIsCreating(false);
    if (result.ok && result.track) {
      setShowCreateModal(false);
      setNewTrackCycleId(''); setNewTrackName(''); setNewTrackDescription('');
      setCreateMode('cycle_linked');
      navigate(`/prioritization/tracks/${result.track.id}`);
    } else {
      setCreateError(result.error || 'Failed to create track.');
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateError(null);
    setCreateMode('cycle_linked');
    setNewTrackCycleId('');
    setNewTrackName('');
    setNewTrackDescription('');
  };

  const cycleMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of cycles) map[c.id] = c.name;
    return map;
  }, [cycles]);

  const sessionCountByTrack = useMemo(() => {
    const map: Record<string, number> = {};
    for (const s of sessions) {
      if (s.trackId) map[s.trackId] = (map[s.trackId] ?? 0) + 1;
    }
    return map;
  }, [sessions]);

  const activeCycleName = useMemo(
    () => cycles.find(c => c.id === activeCycleId)?.name ?? pinnedCycleName ?? 'Selected cycle',
    [cycles, activeCycleId, pinnedCycleName],
  );

  // Pre-filter by active cycle (URL param / pinned).
  // Standalone tracks are always shown when no cycle filter is active.
  const baseTracks = useMemo(() => {
    const base = tracks.filter(t => t.status !== 'archived');
    return activeCycleId ? base.filter(t => t.cycleId === activeCycleId) : base;
  }, [tracks, activeCycleId]);

  // ── List state ──────────────────────────────────────────────────────────────
  const listState = useEntityListState({
    allColumns: ALL_COLUMNS,
    defaultSortField: DEFAULT_SORT_FIELD,
    defaultSortDirection: DEFAULT_SORT_DIR,
    storageKey: 'tracks',
    filterFields: FILTER_FIELDS,
  });

  // ── Filtered + sorted items ─────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let result = baseTracks;
    if (listState.searchQuery) result = result.filter(t => matchesSearch(t, listState.searchQuery, cycleMap));
    result = listState.applyFilters(result, (track, field) => {
      if (field === 'status') return track.status;
      return undefined;
    });
    result = sortTracks(result, listState.sort.field, listState.sort.direction, cycleMap);
    return result;
  }, [baseTracks, listState.searchQuery, listState.sort, listState.applyFilters, cycleMap]);

  // ── Grouping ────────────────────────────────────────────────────────────────
  const groupedItems = useMemo(() => {
    if (!listState.groupBy) return null;
    const groups: Map<string, Track[]> = new Map();
    for (const track of filtered) {
      let key = '';
      if (listState.groupBy === 'cycleId') key = track.cycleId ?? '';
      else if (listState.groupBy === 'status') key = track.status;
      const arr = groups.get(key) ?? [];
      arr.push(track);
      groups.set(key, arr);
    }
    return groups;
  }, [filtered, listState.groupBy]);

  const groupLabel = (key: string): string => {
    if (listState.groupBy === 'cycleId') return key ? (cycleMap[key] ?? 'Unknown Cycle') : 'Unbound (Standalone)';
    return key;
  };

  const rowHeight = listState.density === 'compact' ? 40 : listState.density === 'comfortable' ? 72 : 56;

  // ── Table columns ───────────────────────────────────────────────────────────
  const tableColumns = useMemo((): VirtualizedTableColumn<Track>[] => {
    const colMap: Record<string, VirtualizedTableColumn<Track>> = {
      name: {
        key: 'name', label: 'Track', width: '30%', sortable: true,
        render: (track) => {
          const isPinned = pinnedTrackId === track.id;
          return (
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={e => { e.stopPropagation(); if (isPinned) { exitTrack(); } else { enterTrack(track.id, track.name, track.cycleId ?? '', cycleMap[track.cycleId ?? ''] ?? ''); navigate(`/prioritization/tracks/${track.id}`); } }}
                aria-label={isPinned ? `Leave ${track.name}` : `Work in ${track.name}`}
                className={`flex-shrink-0 p-0.5 transition-colors ${FOCUS_RING} ${isPinned ? 'text-[var(--mustard)]' : 'text-[var(--text-ter)] hover:text-[var(--mustard)]'}`}>
                <Star className={`h-3.5 w-3.5 ${isPinned ? 'fill-sky-400' : ''}`} />
              </button>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Link to={`/prioritization/tracks/${track.id}`} onClick={e => e.stopPropagation()} className="text-sm font-medium text-[var(--text-pri)] hover:text-[var(--mustard)] truncate">{track.name}</Link>
                  {isPinned && <span {...status('mustard', 'text-[0.7rem]')}>Active</span>}
                </div>
                {track.description && listState.density !== 'compact' && <p className="text-xs text-[var(--text-ter)] truncate mt-0.5">{track.description}</p>}
              </div>
            </div>
          );
        },
      },
      cycle: {
        key: 'cycle', label: 'Cycle', width: '22%', sortable: true,
        render: (track) => track.cycleId ? (
          <Link to={`/prioritization/cycles/${track.cycleId}`} onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1.5 text-sm text-[var(--text-sec)] hover:text-[var(--mustard)] transition-colors">
            <RefreshCcw className="h-3.5 w-3.5 text-[var(--text-ter)]" />{cycleMap[track.cycleId] ?? 'Unknown'}
          </Link>
        ) : (
          <span {...chip('mustard')}>
            <Unlink className="h-3 w-3" />Unbound
          </span>
        ),
      },
      sessions: {
        key: 'sessions', label: 'Sessions', width: '9%',
        render: (track) => (
          <span className="inline-flex items-center gap-1.5 text-sm text-[var(--text-sec)]"><Target className="h-3.5 w-3.5 text-[var(--text-ter)]" />{sessionCountByTrack[track.id] ?? 0}</span>
        ),
      },
      status: {
        key: 'status', label: 'Status', width: '10%', sortable: true,
        render: (track) => <TrackStatusBadge status={track.status} />,
      },
      createdAt: {
        key: 'createdAt', label: 'Created', width: '10%', sortable: true,
        render: (track) => <span className="text-sm text-[var(--text-ter)]">{formatDate(track.createdAt)}</span>,
      },
      updatedAt: {
        key: 'updatedAt', label: 'Modified', width: '10%', sortable: true,
        render: (track) => <span className="text-sm text-[var(--text-ter)]">{formatDate(track.updatedAt)}</span>,
      },
    };
    return listState.visibleColumns.map(key => colMap[key]).filter(Boolean) as VirtualizedTableColumn<Track>[];
  }, [listState.visibleColumns, listState.density, pinnedTrackId, cycleMap, sessionCountByTrack, enterTrack, exitTrack, navigate]);

  // ── List row ────────────────────────────────────────────────────────────────
  const renderListRow = (track: Track) => {
    const isPinned = pinnedTrackId === track.id;
    const sessionCount = sessionCountByTrack[track.id] ?? 0;
    return (
      <div
        key={track.id}
        onClick={() => navigate(`/prioritization/tracks/${track.id}`)}
        className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${isPinned ? 'bg-[var(--active-tint)]' : 'hover:bg-[var(--hover-tint)]'}`}
      >
        <button
          onClick={e => { e.stopPropagation(); if (isPinned) { exitTrack(); } else { enterTrack(track.id, track.name, track.cycleId ?? '', cycleMap[track.cycleId ?? ''] ?? ''); navigate(`/prioritization/tracks/${track.id}`); } }}
          aria-label={isPinned ? `Leave ${track.name}` : `Work in ${track.name}`}
          className={`flex-shrink-0 p-1 transition-colors ${FOCUS_RING} ${isPinned ? 'text-[var(--mustard)]' : 'text-[var(--text-ter)] hover:text-[var(--mustard)]'}`}
        >
          <Star className={`h-3.5 w-3.5 ${isPinned ? 'fill-sky-400' : ''}`} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-[var(--text-pri)] truncate">{track.name}</span>
            <TrackStatusBadge status={track.status} />
            {track.isStandalone && (
              <span {...chip('mustard', 'text-[0.68rem] px-1.5 py-0.5')}>
                <Unlink className="h-2.5 w-2.5" />Unbound
              </span>
            )}
            {isPinned && <span {...status('mustard', 'text-[0.68rem]')}>Active</span>}
          </div>
          <div className="mt-0.5 flex items-center gap-3 text-xs text-[var(--text-ter)] flex-wrap">
            {track.cycleId ? (
              <span className="flex items-center gap-1">
                <RefreshCcw className="h-3 w-3" />
                <Link to={`/prioritization/cycles/${track.cycleId}`} onClick={e => e.stopPropagation()} className="hover:text-[var(--mustard)] transition-colors">
                  {cycleMap[track.cycleId] ?? 'Unknown cycle'}
                </Link>
              </span>
            ) : (
              <span className="flex items-center gap-1 text-[var(--mustard)]"><Unlink className="h-3 w-3" />No cycle — preparatory</span>
            )}
            <span className="flex items-center gap-1"><Target className="h-3 w-3" />{sessionCount} session{sessionCount !== 1 ? 's' : ''}</span>
            <span>Updated {formatDate(track.updatedAt)}</span>
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
      </div>
    );
  };

  const renderGroup = (groupKey: string, items: Track[], mode: 'list' | 'table') => (
    <div key={groupKey}>
      <div
        {...panelInset('flex items-center gap-2 px-4 py-2 border-x-0 border-t-0 cursor-pointer hover:bg-[var(--hover-tint)] transition-colors')}
        onClick={() => listState.toggleGroup(groupKey)}
      >
        <ChevronRight className={`h-3.5 w-3.5 text-[var(--text-ter)] transition-transform ${listState.isGroupOpen(groupKey) ? 'rotate-90' : ''}`} />
        <span className={LABEL_MONO}>{groupLabel(groupKey)}</span>
        <span className="text-xs text-[var(--text-ter)] ml-1">{items.length}</span>
      </div>
      {listState.isGroupOpen(groupKey) && (
        mode === 'table' ? (
          <VirtualizedTable
            items={items} getId={t => t.id} columns={tableColumns} rowHeight={rowHeight}
            selectedIds={new Set()} onToggle={() => {}} selectable={false}
            onRowClick={t => navigate(`/prioritization/tracks/${t.id}`)}
            currentSort={{ field: listState.sort.field as never, direction: listState.sort.direction }}
            onSortChange={field => listState.toggleSort(field as string)}
            height={Math.min(items.length * rowHeight + 40, 600)}
          />
        ) : (
          <div className="divide-y divide-[var(--rule)]">{items.map(renderListRow)}</div>
        )
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <SharedPageHeader
        icon={<Layers className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Tracks"
        subtitle="Bounded contexts where items can be legitimately compared. Each Track defines which items compete together and under what shared conditions."
        actions={
          <button onClick={() => setShowCreateModal(true)} {...btnPrimary()}>
            <Plus className="h-4 w-4" />{CREATE_TRACK_LABEL}
          </button>
        }
      />

      {/* Active cycle filter chip */}
      {activeCycleId && (
        <div className="flex items-center gap-2">
          <span {...chip('mustard')}>
            Filtered to: {activeCycleName}
            <button
              type="button"
              aria-label="Clear cycle filter"
              onClick={() => { setSearchParams(p => { p.delete('cycleId'); return p; }); usePinnedCycleStore.getState().exitWorkspace(); }}
              className={`ml-1 transition-opacity hover:opacity-70 ${FOCUS_RING}`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        </div>
      )}

      <EntityListToolbar
        searchQuery={listState.searchQuery}
        onSearchChange={listState.setSearchQuery}
        searchPlaceholder="Search Tracks…"
        filterFields={FILTER_FIELDS}
        activeFilters={listState.activeFilters}
        onFilterChange={listState.setFieldFilter}
        onClearFilters={listState.clearAllFilters}
        filterCount={listState.filterCount}
        filterPanelOpen={listState.filterPanelOpen}
        onToggleFilterPanel={listState.toggleFilterPanel}
        sort={listState.sort}
        onSortChange={listState.setSort}
        sortOptions={SORT_OPTIONS}
        groupBy={listState.groupBy}
        onGroupByChange={listState.setGroupBy}
        groupOptions={GROUP_OPTIONS}
        allColumns={ALL_COLUMNS}
        visibleColumns={listState.visibleColumns}
        onToggleColumn={listState.toggleColumn}
        onReorderColumns={listState.reorderColumns}
        density={listState.density}
        onDensityChange={listState.setDensity}
        viewMode={listState.viewMode}
        onViewModeChange={listState.setViewMode}
        totalCount={baseTracks.length}
        filteredCount={filtered.length}
      />

      {/* Content */}
      {allTracksStatus === 'error' && tracks.length > 0 && (
        <Callout
          tone="coral"
          title="Tracks could not be refreshed"
          action={<button type="button" onClick={() => void fetchAllTracks()} {...btnSecondary()}>Retry</button>}
        >
          Showing the list from the last load. {allTracksError}
        </Callout>
      )}
      {allTracksStatus === 'error' && tracks.length === 0 ? (
        <div {...panel()}>
          <Callout
            tone="coral"
            title="Tracks could not be loaded"
            action={<button type="button" onClick={() => void fetchAllTracks()} {...btnSecondary()}>Retry</button>}
          >
            {allTracksError}
          </Callout>
        </div>
      ) : (allTracksStatus === 'loading' || allTracksStatus === 'idle') && tracks.length === 0 ? (
        <div {...panel('p-12 text-center')}>
          <p className="text-sm text-[var(--text-ter)]">Loading Tracks…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div {...panel()}>
          <EmptyState
            title={listState.searchQuery ? 'No Tracks match your search' : 'No Tracks yet'}
            description={listState.searchQuery
              ? 'Try a different search term.'
              : 'Create a standalone Track to stage configuration before binding to a cycle, or create one inside a cycle.'}
          />
        </div>
      ) : listState.viewMode === 'table' ? (
        <div {...panel('overflow-hidden')}>
          {groupedItems
            ? Array.from(groupedItems.entries()).map(([k, items]) => renderGroup(k, items, 'table'))
            : (
              <VirtualizedTable
                items={filtered} getId={t => t.id} columns={tableColumns} rowHeight={rowHeight}
                selectedIds={new Set()} onToggle={() => {}} selectable={false}
                onRowClick={t => navigate(`/prioritization/tracks/${t.id}`)}
                currentSort={{ field: listState.sort.field as never, direction: listState.sort.direction }}
                onSortChange={field => listState.toggleSort(field as string)}
                height={Math.min(filtered.length * rowHeight + 40, 700)}
              />
            )}
        </div>
      ) : (
        <div {...panel('overflow-hidden divide-y divide-[var(--rule)]')}>
          {groupedItems
            ? Array.from(groupedItems.entries()).map(([k, items]) => renderGroup(k, items, 'list'))
            : filtered.map(renderListRow)}
        </div>
      )}

      {/* Create Track Modal */}
      <Modal
        open={showCreateModal}
        onClose={handleCloseCreateModal}
        label="Tracks"
        title={CREATE_TRACK_DIALOG_TITLE}
        size="md"
        footer={
          <>
            <button type="button" onClick={handleCloseCreateModal} {...btnSecondary()}>Cancel</button>
            <button
              type="button"
              onClick={handleCreateTrack}
              disabled={(createMode === 'cycle_linked' && !newTrackCycleId) || !newTrackName.trim() || isCreating}
              {...btnPrimary()}
            >
              {isCreating ? 'Creating…' : CREATE_TRACK_LABEL}
            </button>
          </>
        }
      >
        {/* Mode toggle — two buttons sharing one box, so the pair reads as one control. */}
        <div className="flex border" style={{ borderColor: 'var(--rule-bright)' }}>
          <button
            type="button"
            aria-pressed={createMode === 'cycle_linked'}
            onClick={() => { setCreateMode('cycle_linked'); setNewTrackName(''); }}
            className={`flex-1 px-3 py-2 font-mono text-[0.75rem] uppercase tracking-[0.08em] transition-colors ${FOCUS_RING} ${
              createMode === 'cycle_linked'
                ? 'bg-[var(--slate)] text-white'
                : 'text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)]'
            }`}
          >
            <RefreshCcw className="h-3.5 w-3.5 inline mr-1.5" />
            Cycle-Linked
          </button>
          <button
            type="button"
            aria-pressed={createMode === 'standalone'}
            onClick={() => { setCreateMode('standalone'); setNewTrackCycleId(''); }}
            className={`flex-1 px-3 py-2 font-mono text-[0.75rem] uppercase tracking-[0.08em] transition-colors ${FOCUS_RING} ${
              createMode === 'standalone'
                ? 'bg-[var(--mustard)] text-white'
                : 'text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)]'
            }`}
          >
            <Unlink className="h-3.5 w-3.5 inline mr-1.5" />
            Standalone
          </button>
        </div>

        {createMode === 'standalone' && (
          <Callout tone="mustard" className="mt-4">
            A standalone Track exists outside any cycle. Configure participants, item pool, criteria,
            and sessions in advance. When you&rsquo;re ready to prioritize, bind it to a cycle to
            unlock governance capabilities.
          </Callout>
        )}

        {createError && <Callout tone="coral" className="mt-4">{createError}</Callout>}

        <div className="mt-4 space-y-4">
          {createMode === 'cycle_linked' && (
            <Field label="Cycle" required htmlFor="track-cycle">
              {activeCycleId ? (
                <div {...panelInset('flex items-center justify-between px-3 py-2')}>
                  <span className="flex items-center gap-1.5 text-xs text-[var(--text-ter)]">
                    <Lock className="h-3 w-3" />From active focus
                  </span>
                  <span className="text-sm font-medium text-[var(--text-pri)]">{pinnedCycleName || activeCycleId}</span>
                </div>
              ) : (
                <Select id="track-cycle" value={newTrackCycleId} onChange={e => setNewTrackCycleId(e.target.value)}>
                  <option value="">Select a cycle</option>
                  {activeCycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              )}
            </Field>
          )}
          <Field label="Track Name" required htmlFor="track-name">
            <TextInput
              id="track-name"
              value={newTrackName}
              onChange={e => setNewTrackName(e.target.value)}
              placeholder="e.g., Engineering Features"
            />
          </Field>
          <Field label="Description" htmlFor="track-description">
            <TextArea
              id="track-description"
              value={newTrackDescription}
              onChange={e => setNewTrackDescription(e.target.value)}
              placeholder="Optional description"
              rows={2}
            />
          </Field>
        </div>
      </Modal>
    </div>
  );
}
